import { areaIdFromName } from '../../../alerts/longevity-alerts/longevity-alerts.mapper';
import {
  LongevityAnswerView,
  LongevityQuestionDef,
  LongevityQuestionnaireDef,
} from './longevity-request-assessments.model';

/**
 * Lettura di GET /questionnaire (QuestionnaireDTO) e traduzione delle risposte di un test per
 * l'operatore. Le domande arrivano con il loro testo e le opzioni come mappa testo -> peso; le risposte
 * di GET /assessments/{id} sono già testo, per numero di domanda ("F3": "Spesso"). Stesso approccio
 * difensivo della Web App: formato non riconosciuto -> errore, non dati sbagliati.
 */
export class LongevityFormatError extends Error {
  constructor(message: string) {
    super(`Longevity: ${message}`);
    this.name = 'LongevityFormatError';
  }
}

/** Etichetta della scelta -1 se il backend non la manda (`preferNotToAnswer`). */
const PREFER_NOT_TO_ANSWER_LABEL = 'Preferisco non rispondere';

type Raw = Record<string, any>;

const isObject = (value: unknown): value is Raw =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inlineMarkdownToHtml = (text: string): string =>
  escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

/**
 * `comment` e `plan` sono salvati come Markdown "leggero" e la Web App li mostra con il suo
 * `simpleMarkdown` (titoli `#`/`##`/`###`, **grassetto**, elenchi `- `, riga vuota = stacco), che
 * non interpreta HTML: l'editor Quill lavora in HTML, quindi qui si converte nei due sensi con la
 * stessa sintassi. Quello che il Markdown non sa esprimere (corsivo, sottolineato...) non è nella toolbar.
 * La Web App rende `#`, `##` e `###` con lo stesso stile, quindi il titolo ha un solo livello: in
 * lettura qualunque livello diventa `<h3>`, in scrittura si salva sempre `##` (come il testo generato).
 */
export function markdownToEditorHtml(markdown: string | string[]): string {
  const text = Array.isArray(markdown) ? markdown.join('\n\n') : markdown;
  let html = '';
  let inList = false;
  const closeList = (): void => {
    if (inList) {
      html += '</ul>';
      inList = false;
    }
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      closeList();
    } else if (/^#{1,3}[\s\d]/.test(line) || /^#\d+[.)]\s/.test(line)) {
      closeList();
      const title = line.replace(/^#{1,3}\d*[.)?\s]*/u, '').trim();
      html += `<h3>${inlineMarkdownToHtml(title)}</h3>`;
    } else if (/^[-•*]\s/.test(line)) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li>${inlineMarkdownToHtml(line.substring(2).trim())}</li>`;
    } else {
      closeList();
      html += `<p>${inlineMarkdownToHtml(line)}</p>`;
    }
  }
  closeList();
  return html;
}

function inlineHtmlToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (!(node instanceof HTMLElement) || node.classList.contains('ql-ui')) {
    return '';
  }
  if (node.tagName === 'BR') {
    return '\n';
  }
  const inner = Array.from(node.childNodes).map(inlineHtmlToMarkdown).join('');
  if (node.tagName !== 'STRONG' && node.tagName !== 'B') {
    return inner;
  }
  // Gli spazi a bordo del grassetto vanno fuori dai `**`: Quill li mette spesso dentro, e `**testo **` non
  // sarebbe grassetto valido; senza questo "bold - " + "normale" diventerebbe "**bold -**normale".
  const [, leading, text, trailing] = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner)!;
  return text ? `${leading}**${text}**${trailing}` : inner;
}

/** HTML dell'editor -> Markdown da salvare. Editor vuoto (`<p><br></p>`) -> stringa vuota. */
export function editorHtmlToMarkdown(html: string | null): string {
  const body = new DOMParser().parseFromString(html ?? '', 'text/html').body;
  const blocks: string[] = [];

  body.childNodes.forEach((node) => {
    // Quill 2 serializza anche gli elenchi puntati come <ol><li data-list="bullet">.
    if (node instanceof HTMLElement && (node.tagName === 'UL' || node.tagName === 'OL')) {
      const items = Array.from(node.children)
        .map((li, index) => {
          const text = inlineHtmlToMarkdown(li).trim();
          const bullet = node.tagName === 'UL' || li.getAttribute('data-list') === 'bullet';
          return text ? `${bullet ? '-' : `${index + 1}.`} ${text}` : '';
        })
        .filter(Boolean);
      if (items.length) {
        blocks.push(items.join('\n'));
      }
      return;
    }
    const text = inlineHtmlToMarkdown(node)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n');
    if (!text) {
      return;
    }
    const isHeading = node instanceof HTMLElement && /^H[1-6]$/.test(node.tagName);
    blocks.push(isHeading ? `## ${text}` : text);
  });

  return blocks.join('\n\n');
}

function normalizeQuestion(raw: unknown): LongevityQuestionDef {
  if (
    !isObject(raw) ||
    typeof raw['text'] !== 'string' ||
    !isObject(raw['options']) ||
    toNumberOrNull(raw['number']) === null
  ) {
    throw new LongevityFormatError('domanda del questionario non riconosciuta');
  }
  return {
    code: String(raw['code'] ?? ''),
    number: toNumberOrNull(raw['number']) as number,
    scored: raw['scored'] === true,
    area: raw['area'] ? areaIdFromName(raw['area']) : null,
    text: raw['text'].trim(),
    options: Object.keys(raw['options']),
  };
}

export function normalizeQuestionnaire(raw: unknown): LongevityQuestionnaireDef {
  if (!isObject(raw) || !Array.isArray(raw['questions'])) {
    throw new LongevityFormatError('questionario non riconosciuto');
  }
  const preferNotToAnswer = isObject(raw['preferNotToAnswer']) ? Object.keys(raw['preferNotToAnswer'])[0] : undefined;
  return {
    rulesVersion: String(raw['rulesVersion'] ?? ''),
    preferNotToAnswer: preferNotToAnswer ?? PREFER_NOT_TO_ANSWER_LABEL,
    questions: raw['questions'].map(normalizeQuestion).sort((a, b) => a.number - b.number),
  };
}

/** "F5" / "5" / 5 -> 5. */
function questionNumberFromKey(key: unknown): number | null {
  const match = /^F?(\d{1,2})$/i.exec(String(key ?? '').trim());
  return match ? Number(match[1]) : null;
}

/**
 * Risposte del dettaglio -> righe pronte per il template, nell'ordine del questionario.
 * `answers` è la mappa del backend {"F3": "Spesso"}: chiave = "F" + numero di domanda, valore = testo
 * dell'opzione scelta; una domanda in bianco non ha la chiave e "Preferisco non rispondere" può arrivare
 * come testo o come -1. Senza questionario (es. non accessibile all'operatore) si ripiega sulle sole
 * chiavi e risposte grezze.
 */
export function buildAnswerViews(
  questionnaire: LongevityQuestionnaireDef | null,
  answers: Record<string, string>
): LongevityAnswerView[] {
  const prefer = questionnaire?.preferNotToAnswer ?? PREFER_NOT_TO_ANSWER_LABEL;
  const isPreferNotToAnswer = (value: string): boolean => value === '-1' || value === prefer;

  if (!questionnaire) {
    return Object.entries(answers)
      .sort(([a], [b]) => (questionNumberFromKey(a) ?? 0) - (questionNumberFromKey(b) ?? 0))
      .map(([key, value]) => ({
        key,
        number: questionNumberFromKey(key) ?? 0,
        area: 'OTHER' as const,
        text: key,
        selectedLabel: isPreferNotToAnswer(value) ? prefer : value,
        unanswered: isPreferNotToAnswer(value),
      }));
  }
  return questionnaire.questions.map((question) => {
    const key = `F${question.number}`;
    const value = answers[key];
    // Chiave assente = domanda lasciata in bianco; il rifiuto esplicito è un'altra cosa.
    const refused = value !== undefined && isPreferNotToAnswer(value);
    return {
      key,
      number: question.number,
      area: question.area ?? ('CONTEXT' as const),
      text: question.text,
      selectedLabel: value === undefined ? 'Non risposto' : refused ? prefer : value,
      unanswered: value === undefined || refused,
    };
  });
}
